import { baseUrlEndpoint, resolveBaseUrl } from "../baseUrl";
import { oauthAuth } from "../standardAuthPatterns";
import { defineConnector } from "../types";
import { exactUrlToRegex } from "../urlPatternConversion";
import logo from "./logo.svg";

const defaultBaseUrl = "https://mygraph.oneplan.ai";

export const onePlan = defineConnector({
  identifier: "onePlan",
  name: "OnePlan",
  tagLine: "Strategic portfolio and work management",
  keywords: "one plan projects portfolios resources tasks planning",
  logo,
  protocol: "http",
  tags: ["productivity"],
  defaultUrlPattern: ({ baseUrl }) =>
    exactUrlToRegex(baseUrlEndpoint(baseUrl, defaultBaseUrl, "/api")),
  auth: oauthAuth,
  steps: [
    {
      title: "Enter your OnePlan URL",
      items: [
        {
          kind: "baseUrl",
          label: "OnePlan URL",
          required: true,
          defaultValue: defaultBaseUrl,
          placeholder: defaultBaseUrl,
          helperText:
            "Use https://mygraph.oneplan.ai for accounts on my.oneplan.ai. For another region, enter its OnePlan URL, such as https://eu.oneplan.ai, without a group ID or API path.",
          target: { kind: "configuration", field: "baseUrl" },
        },
      ],
    },
    {
      title: "Register a OnePlan Custom App",
      items: [
        {
          kind: "select",
          label: "Client type",
          required: true,
          defaultValue: "confidential",
          options: [
            { value: "confidential", label: "Confidential (client secret)" },
            { value: "public", label: "Public (no client secret)" },
          ],
          helperText: "Match the client type of your OnePlan Custom App.",
          target: { kind: "configuration", field: "clientType" },
        },
        {
          kind: "markdown",
          content: ({ clientType }: Record<string, string>) =>
            `As a group administrator, open your profile menu in OnePlan and select **Register Custom Apps › + › New App Registration**. Enter an application name and the callback URL below. Choose **Group** scope for the current group or **Organization** to share the app across groups. ${clientType === "public" ? "Enable **Public client** and **Require PKCE**, then select **Add** and copy the client ID." : "Leave **Public client** disabled and enable **Require PKCE**, then select **Add** and copy the client ID and client secret. The secret is shown only once."} See [OnePlan’s registration guide](https://support.oneplan.ai/hc/en-us/articles/46547723250189-How-to-Register-a-Custom-App-in-OnePlan).`,
        },
        {
          kind: "copyable",
          label: "Callback URL",
          value: "{API_URL}/connected-apps/oauth/onePlan/callback",
        },
        {
          kind: "markdown",
          content:
            "For an Organization app, enable it in each group through **Register Custom Apps › + › Browse App Registrations › Organization › Add Consent**.",
        },
      ],
    },
    {
      title: "Enter your app’s credentials",
      items: [
        {
          kind: "input",
          label: "Client ID",
          required: true,
          target: { kind: "oauthConnection", field: "clientId" },
        },
        {
          kind: "input",
          label: "Client secret",
          required: true,
          target: { kind: "oauthConnection", field: "clientSecret" },
          visibleWhen: [
            {
              target: { kind: "configuration", field: "clientType" },
              notValue: "public",
            },
          ],
        },
      ],
    },
    {
      title: "Authorize your OnePlan account",
      items: [
        {
          kind: "markdown",
          content:
            "Sign in and select the group this connector should access. API access follows your OnePlan user’s permissions in that group.",
        },
        {
          kind: "oauth",
          dependsOn: [
            { kind: "configuration", field: "baseUrl" },
            { kind: "configuration", field: "clientType" },
            { kind: "oauthConnection", field: "clientId" },
            { kind: "oauthConnection", field: "clientSecret" },
          ],
        },
      ],
    },
  ],
  oauth: {
    grantType: () => "authorization_code",
    authorizationEndpoint: ({ baseUrl }) =>
      baseUrlEndpoint(baseUrl, defaultBaseUrl, "/authorize"),
    tokenEndpoint: ({ baseUrl }) =>
      baseUrlEndpoint(baseUrl, defaultBaseUrl, "/token"),
    tokenEndpointAuthMethod: ({ clientType }) =>
      clientType === "public" ? "none" : "client_secret_basic",
    clientCredentialsFrom: "db",
    scopes: () => [],
    pkce: () => true,
  },
  connectionTest: {
    protocol: "http",
    request: ({ baseUrl }) => ({
      url: baseUrlEndpoint(baseUrl, defaultBaseUrl, "/api/resources/me"),
      headers: { Accept: "application/json" },
      expectedBodyContains: '"userid"',
    }),
  },
  publicEnvVars: ({ baseUrl }) => ({
    ONEPLAN_URL: resolveBaseUrl(baseUrl, defaultBaseUrl),
  }),
});
